// ABOUTME: Renders the applicant WhatsApp panel with messages, consent, media, and send actions.
// ABOUTME: Connects applicant profile context to WhatsApp conversation APIs and realtime updates.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    Paper,
    TextField,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import RefreshIcon from "@material-ui/icons/Refresh";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import DescriptionIcon from "@material-ui/icons/Description";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import { useQueryClient } from "react-query";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    buildWhatsappContactEpisodeActionRequest,
    canSaveWhatsappContactEpisodeMedia,
    createWhatsappActionRequestTracker,
    getWhatsappContactEpisodeActionContext,
    getWhatsappContactEpisodeActionEligibility,
    getWhatsappContactEpisodeActionContextForMode,
    getWhatsappContactEpisodeActionContexts,
    normalizeWhatsappContactEpisodeMode,
    resolveWhatsappContactEpisodeActionContextSelection,
    WhatsappContactEpisodeActionContext
} from "functions/whatsappContactEpisode";
import { transformDBData } from "functions/util";
import useApplicantViewContext from "../useApplicantViewContext";
import {
    useAuthorization,
    useAuthState
} from "_ReactContext/AuthContext";
import {
    canUseApplicantWhatsappBaseActions,
    getApiErrorMessage,
    getWhatsappProfileStatusLabel
} from "./helpers";
import ApplicantWhatsappMediaActions from "./ApplicantWhatsappMediaActions";
import { useWhatsappConversation } from "./useWhatsappConversation";
import WhatsappTemplateSendDialog, {
    normalizeWhatsappTemplateJobId
} from "Pages/WhatsappTemplateSendDialog";
import {
    canSendWhatsappSessionMessage,
    formatWhatsappInboxDate,
    formatWhatsappReactionText
} from "Pages/WhatsappInbox/helpers";
import axios from "axios";
import { useTranslation } from "react-i18next";
import WhatsappResolvedContactEpisodeControls from "Pages/WhatsappInbox/WhatsappResolvedContactEpisodeControls";
import { useVerifyJobAccess } from "Pages/Job/util/useVerifyJobAccess";

const whatsappInboxModuleName = "WHATSAPPINBOX";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        gridContainer: {
            height: "90%"
        },
        container: {
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            height: "100%",
            backgroundColor: theme.palette.backgroundGrey.main,
            overflow: "auto"
        },
        paperContainer: {
            margin: theme.spacing(2),
            padding: theme.spacing(2)
        },
        chipsContainer: {
            gap: theme.spacing(1),
            display: "flex",
            flexWrap: "wrap",
            marginTop: theme.spacing(1)
        },
        messageList: {
            padding: 0
        },
        messageBubble: {
            borderRadius: theme.spacing(1.5),
            maxWidth: "80%",
            padding: theme.spacing(1.5),
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
        },
        outgoingBubble: {
            backgroundColor: "#DCF8C6"
        },
        incomingBubble: {
            backgroundColor: theme.palette.background.paper
        },
        messageMeta: {
            marginTop: theme.spacing(1),
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(1),
            alignItems: "center"
        },
        messageActions: {
            marginTop: theme.spacing(1),
            display: "flex",
            gap: theme.spacing(1),
            flexWrap: "wrap"
        },
        emptyState: {
            margin: theme.spacing(2)
        },
        textField: {
            marginTop: theme.spacing(2)
        },
        conversationFooterActions: {
            display: "flex",
            justifyContent: "flex-end",
            padding: theme.spacing(1.5, 2, 2)
        }
    })
);

const getLabels = (language: string) => {
    const isPortuguese = (language || "").toLowerCase().startsWith("pt");

    if (isPortuguese) {
        return {
            title: "WhatsApp",
            refresh: "Atualizar conversa",
            sendTemplate: "Enviar template",
            sendSessionMessage: "Enviar mensagem",
            notConnected: "Empresa sem conexão ativa",
            connected: "Conectado",
            disconnected: "Desconectado",
            liveConnected: "Atualização em tempo real ativa",
            liveDisconnected: "Atualização em tempo real desconectada",
            optedIn: "Opt-in ativo",
            optedOut: "Opt-out ativo",
            marketingOptedOut: "Opt-out de oportunidades",
            phoneSuppressed: "Telefone bloqueado",
            noOptIn: "Sem opt-in",
            sessionWindowOpen: "Janela de 24h aberta",
            sessionWindowClosed: "Janela de 24h fechada",
            featureDisabled: "WhatsApp desabilitado para esta empresa",
            noMessages:
                "Nenhuma mensagem de WhatsApp encontrada para este candidato.",
            loadError: "Não foi possível carregar a conversa de WhatsApp.",
            connectionUnavailable:
                "A empresa ainda não conectou uma conta do WhatsApp Business.",
            disableOptIn: "Registrar opt-out",
            enableOptIn: "Registrar opt-in",
            consentHistory: "Histórico de consentimento",
            consentDialogOptInTitle: "Registrar opt-in de WhatsApp",
            consentDialogOptOutTitle: "Registrar opt-out de WhatsApp",
            consentDialogOptInText:
                "Registre opt-in apenas quando houver autorização explícita do candidato para contato por WhatsApp.",
            consentDialogOptOutText:
                "O opt-out bloqueia mensagens por WhatsApp. Registre a evidência do pedido do candidato ou da base legal usada para o bloqueio.",
            consentEvidence: "Evidência do consentimento",
            consentEvidenceRequired:
                "Descreva a evidência antes de registrar a preferência.",
            saveConsent: "Registrar",
            recruitingProcessCategory: "Processo seletivo atual",
            futureOpportunitiesCategory: "Futuras oportunidades",
            manualOptInBlocked:
                "Opt-in manual bloqueado porque o candidato fez opt-out recentemente.",
            noConsentEvents: "Nenhum evento de consentimento encontrado.",
            download: "Baixar",
            saveAsDocument: "Salvar em documentos",
            saveAsCv: "Salvar em currículo",
            savedToApplicant: "Arquivo salvo no cadastro",
            templateDialogTitle: "Enviar template de WhatsApp",
            sessionDialogTitle: "Enviar mensagem de sessão",
            close: "Fechar",
            send: "Enviar",
            templateField: "Template aprovado",
            sessionMessageField: "Mensagem",
            sessionCaptionField: "Legenda",
            attachFile: "Anexar arquivo",
            removeAttachment: "Remover anexo",
            unsupportedTemplate:
                "Este template possui componentes ainda não suportados nesta primeira versão.",
            requiredMessage: "Preencha a mensagem antes de enviar.",
            requiredTemplate: "Selecione um template antes de enviar.",
            templateSent: "Template enviado com sucesso.",
            sessionMessageSent: "Mensagem enviada com sucesso.",
            optInUpdated: "Preferência de opt-in atualizada.",
            mediaSaved: "Arquivo salvo no cadastro do candidato.",
            sendDisabledNoConnection:
                "Conecte uma conta do WhatsApp Business para enviar mensagens.",
            sendDisabledNoOptIn:
                "O candidato não possui opt-in ativo para comunicação via WhatsApp.",
            sendDisabledOptOut:
                "O candidato registrou opt-out para comunicação via WhatsApp.",
            sendDisabledNoWindow:
                "A janela de atendimento de 24 horas está fechada para este candidato.",
            retry: "Tentar novamente",
            mediaType: "Mídia",
            directionIncoming: "Recebida",
            directionOutgoing: "Enviada",
            status: "Status",
            statusAccepted: "Aceita",
            statusDeleted: "Excluída",
            statusDelivered: "Entregue",
            statusFailed: "Falhou",
            statusRead: "Lida",
            statusReceived: "Recebida",
            statusSent: "Enviada",
            statusUnknown: "-",
            reactedWith: "Reagiu com {{emoji}}",
            templateName: "Template",
            templateCategory: "Categoria",
            parameter: "Parâmetro",
            headerParameter: "Parâmetro do cabeçalho",
            bodyParameter: "Parâmetro do corpo"
        };
    }

    return {
        title: "WhatsApp",
        refresh: "Refresh conversation",
        sendTemplate: "Send template",
        sendSessionMessage: "Send message",
        notConnected: "Company has no active connection",
        connected: "Connected",
        disconnected: "Disconnected",
        liveConnected: "Realtime updates connected",
        liveDisconnected: "Realtime updates disconnected",
        optedIn: "Opt-in enabled",
        optedOut: "Opt-out enabled",
        marketingOptedOut: "Opportunities opt-out",
        phoneSuppressed: "Phone suppressed",
        noOptIn: "No opt-in",
        sessionWindowOpen: "24-hour window open",
        sessionWindowClosed: "24-hour window closed",
        featureDisabled: "WhatsApp is disabled for this company",
        noMessages: "No WhatsApp messages were found for this applicant.",
        loadError: "Could not load the WhatsApp conversation.",
        connectionUnavailable:
            "The company has not connected a WhatsApp Business account yet.",
        disableOptIn: "Mark opt-out",
        enableOptIn: "Mark opt-in",
        consentHistory: "Consent history",
        consentDialogOptInTitle: "Register WhatsApp opt-in",
        consentDialogOptOutTitle: "Register WhatsApp opt-out",
        consentDialogOptInText:
            "Register opt-in only when the candidate explicitly authorized WhatsApp contact.",
        consentDialogOptOutText:
            "Opt-out blocks WhatsApp messages. Record the evidence for the candidate request or the legal basis used for the block.",
        consentEvidence: "Consent evidence",
        consentEvidenceRequired:
            "Describe the evidence before registering the preference.",
        saveConsent: "Register",
        recruitingProcessCategory: "Current recruiting process",
        futureOpportunitiesCategory: "Future opportunities",
        manualOptInBlocked:
            "Manual opt-in is blocked because the candidate opted out recently.",
        noConsentEvents: "No consent events found.",
        download: "Download",
        saveAsDocument: "Save to documents",
        saveAsCv: "Save to CV",
        savedToApplicant: "File saved on the applicant profile",
        templateDialogTitle: "Send WhatsApp template",
        sessionDialogTitle: "Send session message",
        close: "Close",
        send: "Send",
        templateField: "Approved template",
        sessionMessageField: "Message",
        sessionCaptionField: "Caption",
        attachFile: "Attach file",
        removeAttachment: "Remove attachment",
        unsupportedTemplate:
            "This template contains components that are not supported in the first version.",
        requiredMessage: "Enter a message before sending.",
        requiredTemplate: "Select a template before sending.",
        templateSent: "Template sent successfully.",
        sessionMessageSent: "Message sent successfully.",
        optInUpdated: "Opt-in preference updated.",
        mediaSaved: "File saved on the applicant profile.",
        sendDisabledNoConnection:
            "Connect a WhatsApp Business account before sending messages.",
        sendDisabledNoOptIn:
            "The applicant does not have opt-in enabled for WhatsApp communication.",
        sendDisabledOptOut:
            "The applicant opted out of WhatsApp communication.",
        sendDisabledNoWindow:
            "The 24-hour customer service window is closed for this applicant.",
        retry: "Retry",
        mediaType: "Media",
        directionIncoming: "Incoming",
        directionOutgoing: "Outgoing",
        status: "Status",
        statusAccepted: "Accepted",
        statusDeleted: "Deleted",
        statusDelivered: "Delivered",
        statusFailed: "Failed",
        statusRead: "Read",
        statusReceived: "Received",
        statusSent: "Sent",
        statusUnknown: "-",
        reactedWith: "Reacted with {{emoji}}",
        templateName: "Template",
        templateCategory: "Category",
        parameter: "Parameter",
        headerParameter: "Header parameter",
        bodyParameter: "Body parameter"
    };
};

const getConnectionChipColor = (connected: boolean) => {
    return connected ? "#2e7d32" : "#b71c1c";
};

const getLiveChipColor = (liveStatus: string) => {
    return liveStatus === "connected" ? "#1565c0" : "#ef6c00";
};

const getStatusColor = (status: string) => {
    switch (status) {
        case "READ":
            return "#1565c0";
        case "DELIVERED":
            return "#2e7d32";
        case "FAILED":
            return "#b71c1c";
        case "RECEIVED":
            return "#6a1b9a";
        default:
            return "#616161";
    }
};

const isWhatsappMediaMessage = (message: Compleo.IObject) =>
    ["document", "image"].includes(String(message.type || ""));

const isGeneratedWhatsappMediaName = (name: string) =>
    /^whatsapp_wamid/i.test(name);

const getWhatsappMediaDisplayName = (
    message: Compleo.IObject,
    labels: ReturnType<typeof getLabels>
) => {
    const originalFileName = String(
        message.originalFileName || message.mediaOriginalFileName || ""
    ).trim();
    const caption = String(message.caption || message.textContent || "").trim();

    if (originalFileName && !isGeneratedWhatsappMediaName(originalFileName)) {
        return originalFileName;
    }

    if (caption) {
        return caption;
    }

    return `${labels.mediaType}: ${message.type || "-"}`;
};

const ApplicantWhatsapp = () => {
    const classes = useStyles();
    const showMessage = useShowMessage();
    const queryClient = useQueryClient();
    const [data] = useApplicantViewContext();
    const { company, user } = useAuthState();
    const { isFeatureAuthorized } = useAuthorization();
    const verifyJobAccess = useVerifyJobAccess();
    const [contactEpisodeT] = useTranslation(whatsappInboxModuleName, {
        useSuspense: false
    });
    const labels = React.useMemo(
        () => getLabels(data.language),
        [data.language]
    );
    const [templateModalOpen, setTemplateModalOpen] = React.useState(false);
    const [sessionModalOpen, setSessionModalOpen] = React.useState(false);
    const [consentModalAction, setConsentModalAction] = React.useState<
        "OPT_IN" | "OPT_OUT" | ""
    >("");
    const [consentHistoryOpen, setConsentHistoryOpen] = React.useState(false);
    const [sessionMessage, setSessionMessage] = React.useState("");
    const [sessionFile, setSessionFile] = React.useState<File | null>(null);
    const [consentEvidence, setConsentEvidence] = React.useState("");
    const [consentCategories, setConsentCategories] = React.useState<string[]>([
        "RECRUITING_PROCESS",
        "FUTURE_OPPORTUNITIES"
    ]);
    const [isSendingSession, setIsSendingSession] = React.useState(false);
    const [isSavingConsent, setIsSavingConsent] = React.useState(false);
    const [messageActionId, setMessageActionId] = React.useState("");
    const [
        selectedContactEpisodeActionContext,
        setSelectedContactEpisodeActionContext
    ] = React.useState<WhatsappContactEpisodeActionContext | undefined>();
    const sessionFileInputRef = React.useRef<HTMLInputElement | null>(null);
    const actionRequestTrackerRef = React.useRef(
        createWhatsappActionRequestTracker()
    );

    const enabled = company.whatsappEnabled === true;
    const whatsappContactEpisodeMode = normalizeWhatsappContactEpisodeMode(
        company?.whatsappContactEpisodeMode
    );
    const hasJobAccess = data.jobDataInsideJob
        ? verifyJobAccess(
              data.jobDataInsideJob.jobData ||
                  data.jobDataInsideJob.jobDataContext ||
                  {}
          )
        : true;

    const [connectionState, fetchConnection] = useApiCache(
        "/whatsapp/connection",
        "post",
        {
            companyId: data.companyId
        },
        true,
        1000 * 30
    );

    const [consentHistoryState, fetchConsentHistory] = useApiCache(
        "/whatsapp/consenthistory",
        "post",
        {
            companyId: data.companyId,
            applicantId: data.applicantId,
            limit: 50
        },
        false,
        1000 * 60
    );
    const fetchConnectionRef = React.useRef(fetchConnection);

    React.useEffect(() => {
        fetchConnectionRef.current = fetchConnection;
    }, [fetchConnection]);

    const {
        conversationState,
        conversationData,
        liveStatus,
        refreshConversation
    } = useWhatsappConversation({
        applicantId: data.applicantId,
        companyId: data.companyId,
        enabled,
        principal: user,
        canShowApplicants: isFeatureAuthorized("ShowApplicants"),
        hasJobAccess,
        whatsappPreRegistrationEnabled:
            company.whatsappPreRegistrationEnabled === true
    });

    React.useEffect(() => {
        if (enabled) {
            fetchConnectionRef.current({
                companyId: data.companyId
            });
        }
    }, [data.companyId, enabled]);

    const connectionData = getWhatsappApiResponseData(
        connectionState.response
    ) as Compleo.IObject;
    const messages = (conversationData.messages || []) as Compleo.IObject[];
    const contactEpisodeActionContexts = React.useMemo(
        () =>
            getWhatsappContactEpisodeActionContexts(
                conversationData.contactEpisodeActionContexts || messages
            ),
        [conversationData.contactEpisodeActionContexts, messages]
    );
    const refreshedContactEpisodeActionContext =
        resolveWhatsappContactEpisodeActionContextSelection(
            contactEpisodeActionContexts,
            selectedContactEpisodeActionContext
        );
    const activeContactEpisodeActionContext =
        getWhatsappContactEpisodeActionContextForMode(
            whatsappContactEpisodeMode,
            refreshedContactEpisodeActionContext
        );
    const baseConsent = (conversationData.consent || {}) as Compleo.IObject;
    const consentHistoryData = getWhatsappApiResponseData(
        consentHistoryState.response
    ) as Compleo.IObject;
    const consentEvents = (consentHistoryData.events ||
        []) as Compleo.IObject[];
    const actionEligibility = getWhatsappContactEpisodeActionEligibility(
        {
            optedIn: conversationData.optedIn === true,
            optedOut: baseConsent.optedOut === true,
            marketingOptedOut: baseConsent.marketingOptedOut === true,
            phoneSuppressed: baseConsent.phoneSuppressed === true,
            manualOptInBlocked: baseConsent.manualOptInBlocked === true,
            hasActiveCustomerServiceWindow:
                conversationData.hasActiveCustomerServiceWindow === true,
            canSendSessionMessage:
                canSendWhatsappSessionMessage(conversationData),
            canSendTemplate: conversationData.canSendTemplate === true
        },
        activeContactEpisodeActionContext
    );
    const {
        optedIn,
        optedOut,
        marketingOptedOut,
        phoneSuppressed,
        manualOptInBlocked,
        hasActiveCustomerServiceWindow,
        canSendSessionMessage,
        canSendTemplate
    } = actionEligibility;
    const consent = (activeContactEpisodeActionContext?.consent ||
        baseConsent) as Compleo.IObject;
    const canMutate = data.readOnly !== true;
    const canUseBaseConversationActions = canUseApplicantWhatsappBaseActions({
            canMutate,
            conversationData
        });
    const canUseConversationActions = activeContactEpisodeActionContext
        ? canMutate
        : canUseBaseConversationActions;
    const isConnected = connectionData.connected === true;

    React.useEffect(() => {
        setSelectedContactEpisodeActionContext(undefined);
    }, [data.applicantId]);

    React.useEffect(() => {
        if (!selectedContactEpisodeActionContext) {
            return;
        }
        if (!refreshedContactEpisodeActionContext) {
            setSelectedContactEpisodeActionContext(undefined);
        }
    }, [
        refreshedContactEpisodeActionContext,
        selectedContactEpisodeActionContext
    ]);

    React.useEffect(() => {
        if (
            whatsappContactEpisodeMode !== "ACTIVE" &&
            selectedContactEpisodeActionContext
        ) {
            setSelectedContactEpisodeActionContext(undefined);
        }
    }, [
        selectedContactEpisodeActionContext,
        whatsappContactEpisodeMode
    ]);

    React.useEffect(() => {
        if (!canUseConversationActions) {
            setTemplateModalOpen(false);
            setSessionModalOpen(false);
        }

        if (!canUseConversationActions) {
            setConsentModalAction("");
            setConsentHistoryOpen(false);
        }
    }, [canUseConversationActions]);

    const applicantJobOptions = React.useMemo(() => {
        const jobs = Array.isArray(data.applicantData?.Jobs)
            ? data.applicantData.Jobs
            : [];
        const usedJobIds = new Set<string>();
        const addJobOption = (
            jobIdValue: string | undefined,
            titleValue: string | undefined
        ) => {
            const jobId = normalizeWhatsappTemplateJobId(jobIdValue);
            const title = String(titleValue || jobId).trim();

            if (!jobId || usedJobIds.has(jobId)) {
                return null;
            }

            usedJobIds.add(jobId);
            return {
                jobId,
                label: title && title !== jobId ? `${title} (${jobId})` : title
            };
        };
        const options = jobs
            .map((job: Compleo.IObject) =>
                addJobOption(
                    job.JobId || job.jobId || job.pk || job._updatedJobData?.pk,
                    job._updatedJobData?.title ||
                        job.JobTitle ||
                        job.title ||
                        job.jobTitle
                )
            )
            .filter(Boolean) as { jobId: string; label: string }[];

        if (
            data.jobDataInsideJob?.jobId &&
            !options.some(
                (job: { jobId: string }) =>
                    job.jobId ===
                    normalizeWhatsappTemplateJobId(data.jobDataInsideJob?.jobId)
            )
        ) {
            const option = addJobOption(
                data.jobDataInsideJob.jobId,
                data.jobDataInsideJob.jobData?.title ||
                    data.jobDataInsideJob.jobData?.JobTitle ||
                    data.jobDataInsideJob.jobId
            );

            return option ? [option, ...options] : options;
        }

        return options;
    }, [data.applicantData?.Jobs, data.jobDataInsideJob]);

    const refreshAll = () => {
        fetchConnectionRef.current({
            companyId: data.companyId
        });
        refreshConversation();
    };

    const openConsentModal = (action: "OPT_IN" | "OPT_OUT") => {
        setConsentModalAction(action);
        setConsentEvidence("");
        setConsentCategories(
            Array.isArray(consent.allowedCategories) &&
                consent.allowedCategories.length > 0
                ? consent.allowedCategories
                : ["RECRUITING_PROCESS", "FUTURE_OPPORTUNITIES"]
        );
    };

    const handleConsentCategoryChange = (category: string) => {
        setConsentCategories((currentState) => {
            if (currentState.includes(category)) {
                return currentState.filter((item) => item !== category);
            }

            return [...currentState, category];
        });
    };

    const handleSaveConsent = async () => {
        const isOptIn = consentModalAction === "OPT_IN";

        if (!consentEvidence.trim()) {
            showMessage(labels.consentEvidenceRequired, "error");
            return;
        }

        const attempt = actionRequestTrackerRef.current.prepare(
            "SET_CONSENT",
            JSON.stringify({
                companyId: data.companyId,
                applicantId: data.applicantId,
                optedIn: isOptIn,
                categories: isOptIn ? [...consentCategories].sort() : [],
                evidenceText: consentEvidence.trim(),
                jobId: data.jobDataInsideJob?.jobId || "",
                contactEpisodeId:
                    activeContactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    activeContactEpisodeActionContext?.resolutionRevision || 0
            })
        );
        try {
            setIsSavingConsent(true);
            await apiDirectCall(
                "/whatsapp/setoptin",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                companyId: data.companyId,
                applicantId: data.applicantId,
                optedIn: isOptIn,
                categories: isOptIn ? consentCategories : [],
                evidenceChannel: "manual",
                evidenceText: consentEvidence,
                jobId: data.jobDataInsideJob?.jobId
                    },
                    activeContactEpisodeActionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "SET_CONSENT",
                attempt.signature
            );
            showMessage(labels.optInUpdated, "success");
            setConsentModalAction("");
            queryClient.invalidateQueries("/whatsapp/consenthistory");
            fetchConsentHistory({
                companyId: data.companyId,
                applicantId: data.applicantId,
                limit: 50
            });
            refreshConversation();
        } catch (error) {
            showMessage(getApiErrorMessage(error, labels.loadError), "error");
        } finally {
            setIsSavingConsent(false);
        }
    };

    const openConsentHistory = () => {
        setConsentHistoryOpen(true);
        queryClient.invalidateQueries("/whatsapp/consenthistory");
        fetchConsentHistory({
            companyId: data.companyId,
            applicantId: data.applicantId,
            limit: 50
        });
    };

    const handleSaveMedia = async (
        message: Compleo.IObject,
        saveAs: "document" | "cv"
    ) => {
        if (
            !canSaveWhatsappContactEpisodeMedia(
                whatsappContactEpisodeMode,
                message
            )
        ) {
            return;
        }
        const actionContext =
            getWhatsappContactEpisodeActionContextForMode(
                whatsappContactEpisodeMode,
                getWhatsappContactEpisodeActionContext(message)
            );
        const attempt = actionRequestTrackerRef.current.prepare(
            "SAVE_MEDIA",
            JSON.stringify({
                companyId: data.companyId,
                applicantId: data.applicantId,
                messageId: message.messageId,
                segmentId: message.conversationSegmentId || "",
                saveAs,
                contactEpisodeId: actionContext?.contactEpisodeId || "",
                resolutionRevision: actionContext?.resolutionRevision || 0
            })
        );
        try {
            setMessageActionId(`${message.messageId}:${saveAs}`);
            await apiDirectCall(
                "/whatsapp/savemediatoapplicant",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                companyId: data.companyId,
                applicantId: data.applicantId,
                messageId: message.messageId,
                ...(message.conversationSegmentId
                    ? { segmentId: message.conversationSegmentId }
                    : {}),
                saveAs
                    },
                    actionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "SAVE_MEDIA",
                attempt.signature
            );
            queryClient.invalidateQueries(data.queryKey);
            refreshConversation();
            showMessage(labels.mediaSaved, "success");
        } catch (error) {
            showMessage(getApiErrorMessage(error, labels.loadError), "error");
        } finally {
            setMessageActionId("");
        }
    };

    const handleSendSessionMessage = async () => {
        const text = sessionMessage.trim();
        const file = sessionFile;

        if (!text && !file) {
            showMessage(labels.requiredMessage, "error");
            return;
        }

        const attempt = actionRequestTrackerRef.current.prepare(
            "SEND_SESSION",
            JSON.stringify({
                companyId: data.companyId,
                applicantId: data.applicantId,
                text,
                file: file
                    ? {
                          name: file.name,
                          type: file.type,
                          size: file.size,
                          lastModified: file.lastModified
                      }
                    : null,
                jobId: data.jobDataInsideJob?.jobId || "",
                contactEpisodeId:
                    activeContactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    activeContactEpisodeActionContext?.resolutionRevision || 0
            })
        );
        try {
            setIsSendingSession(true);
            const actionRequestId = attempt.actionRequestId;
            if (file) {
                const uploadResponse = await apiDirectCall(
                    "/whatsapp/requestmediaupload",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                    {
                        companyId: data.companyId,
                        applicantId: data.applicantId,
                        originalFileName: file.name,
                        contentType: file.type,
                        fileSize: file.size,
                        jobId: data.jobDataInsideJob?.jobId
                        },
                        activeContactEpisodeActionContext,
                        actionRequestId
                    )
                );
                const uploadData = getWhatsappApiResponseData(uploadResponse);
                if (uploadData.uploadMethod === "POST") {
                    const formData = new FormData();
                    Object.entries(
                        uploadData.uploadFields || {}
                    ).forEach(([key, value]) =>
                        formData.append(key, String(value))
                    );
                    formData.append("file", file);
                    await axios.post(uploadData.uploadURL, formData);
                } else {
                    await axios.put(uploadData.uploadURL, file, {
                        headers: {
                            "Content-Type": uploadData.contentType || file.type,
                            "x-amz-tagging": `originalFileName=${encodeURIComponent(
                                uploadData.originalFileName || file.name
                            )}`
                        }
                    });
                }
                await apiDirectCall(
                    "/whatsapp/sendmedia",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                        {
                    companyId: data.companyId,
                    applicantId: data.applicantId,
                    attachmentId: uploadData.attachmentId,
                    s3Key: uploadData.s3Key,
                            originalFileName:
                                uploadData.originalFileName || file.name,
                    contentType: uploadData.contentType || file.type,
                    fileSize: file.size,
                    caption: text,
                    jobId: data.jobDataInsideJob?.jobId
                        },
                        activeContactEpisodeActionContext,
                        actionRequestId
                    )
                );
            } else {
                await apiDirectCall(
                    "/whatsapp/sendsessionmessage",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                        {
                    companyId: data.companyId,
                    applicantId: data.applicantId,
                    text,
                    jobId: data.jobDataInsideJob?.jobId
                        },
                        activeContactEpisodeActionContext,
                        actionRequestId
                    )
                );
            }
            actionRequestTrackerRef.current.complete(
                "SEND_SESSION",
                attempt.signature
            );
            setSessionModalOpen(false);
            setSessionMessage("");
            setSessionFile(null);
            refreshConversation();
            showMessage(labels.sessionMessageSent, "success");
        } catch (error) {
            showMessage(getApiErrorMessage(error, labels.loadError), "error");
        } finally {
            setIsSendingSession(false);
        }
    };

    const handleDownloadMedia = async (message: Compleo.IObject) => {
        const mediaWindow = window.open("", "_blank");
        const actionContext = getWhatsappContactEpisodeActionContext(message);
        const attempt = actionRequestTrackerRef.current.prepare(
            "OPEN_MEDIA",
            JSON.stringify({
                companyId: data.companyId,
                applicantId: data.applicantId,
                messageId: message.messageId,
                segmentId: message.conversationSegmentId || "",
                contactEpisodeId: actionContext?.contactEpisodeId || "",
                resolutionRevision: actionContext?.resolutionRevision || 0
            })
        );

        try {
            setMessageActionId(`${message.messageId}:download`);
            const response = await apiDirectCall(
                "/whatsapp/mediaurl",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                companyId: data.companyId,
                applicantId: data.applicantId,
                messageId: message.messageId,
                ...(message.conversationSegmentId
                    ? { segmentId: message.conversationSegmentId }
                    : {})
                    },
                    actionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "OPEN_MEDIA",
                attempt.signature
            );
            const mediaData = getWhatsappApiResponseData(response);
            if (mediaWindow) {
                mediaWindow.location.href = mediaData.url;
            } else {
                window.open(mediaData.url, "_blank", "noopener,noreferrer");
            }
        } catch (error) {
            mediaWindow?.close();
            showMessage(getApiErrorMessage(error, labels.loadError), "error");
        } finally {
            setMessageActionId("");
        }
    };

    const handleOpenTemplateDialog = () => {
        if (!isConnected) {
            showMessage(labels.sendDisabledNoConnection, "error");
            return;
        }
        if (optedOut) {
            showMessage(labels.sendDisabledOptOut, "error");
            return;
        }
        if (!canSendTemplate) {
            showMessage(labels.sendDisabledNoOptIn, "error");
            return;
        }

        setTemplateModalOpen(true);
    };

    const handleOpenSessionDialog = () => {
        if (!isConnected) {
            showMessage(labels.sendDisabledNoConnection, "error");
            return;
        }
        if (!canSendSessionMessage) {
            if (optedOut) {
                showMessage(labels.sendDisabledOptOut, "error");
                return;
            }
            showMessage(labels.sendDisabledNoWindow, "error");
            return;
        }

        setSessionModalOpen(true);
    };

    if (!enabled) {
        return null;
    }

    if (conversationState.status === "error") {
        return (
            <Grid container item className={classes.gridContainer}>
                <Box className={classes.container}>
                    <Alert
                        severity="error"
                        className={classes.emptyState}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={refreshAll}
                            >
                                {labels.retry}
                            </Button>
                        }
                    >
                        {labels.loadError}
                    </Alert>
                </Box>
            </Grid>
        );
    }

    if (conversationState.status !== "success") {
        return <Loading />;
    }

    return (
        <Grid container item className={classes.gridContainer}>
            <Box className={classes.container}>
                <Paper className={classes.paperContainer}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                            <Box display="flex" alignItems="center" gridGap={8}>
                                <WhatsAppIcon />
                                <Typography variant="body1">
                                    {labels.title}
                                </Typography>
                            </Box>
                            <Box className={classes.chipsContainer}>
                                <Chip
                                    size="small"
                                    label={
                                        isConnected
                                            ? labels.connected
                                            : labels.disconnected
                                    }
                                    style={{
                                        backgroundColor: getConnectionChipColor(
                                            isConnected
                                        ),
                                        color: "white"
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={
                                        liveStatus === "connected"
                                            ? labels.liveConnected
                                            : labels.liveDisconnected
                                    }
                                    style={{
                                        backgroundColor: getLiveChipColor(
                                            liveStatus
                                        ),
                                        color: "white"
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={
                                        optedIn
                                            ? labels.optedIn
                                            : optedOut
                                              ? labels.optedOut
                                              : labels.noOptIn
                                    }
                                />
                                {marketingOptedOut && !optedOut && (
                                    <Chip
                                        size="small"
                                        label={labels.marketingOptedOut}
                                    />
                                )}
                                {phoneSuppressed && (
                                    <Chip
                                        size="small"
                                        label={labels.phoneSuppressed}
                                    />
                                )}
                                <Chip
                                    size="small"
                                    label={
                                        hasActiveCustomerServiceWindow
                                            ? labels.sessionWindowOpen
                                            : labels.sessionWindowClosed
                                    }
                                />
                            </Box>
                            {!isConnected && (
                                <Box marginTop={2}>
                                    <Alert severity="info">
                                        {labels.connectionUnavailable}
                                    </Alert>
                                </Box>
                            )}
                            {manualOptInBlocked && !optedIn && (
                                <Box marginTop={2}>
                                    <Alert severity="warning">
                                        {labels.manualOptInBlocked}{" "}
                                        {consent.manualOptInBlockedUntil || ""}
                                    </Alert>
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gridGap={8}
                                flexWrap="wrap"
                            >
                                <ButtonIconTooltipCompleo
                                    label={labels.refresh}
                                    onClick={refreshAll}
                                >
                                    <RefreshIcon />
                                </ButtonIconTooltipCompleo>
                                {canUseConversationActions && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        onClick={() =>
                                            openConsentModal(
                                                optedIn ? "OPT_OUT" : "OPT_IN"
                                            )
                                        }
                                        disabled={
                                            !optedIn && manualOptInBlocked
                                        }
                                    >
                                        {optedIn
                                            ? labels.disableOptIn
                                            : labels.enableOptIn}
                                    </Button>
                                )}
                                {canUseConversationActions && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={openConsentHistory}
                                    >
                                        {labels.consentHistory}
                                    </Button>
                                )}
                                {canUseConversationActions &&
                                    !hasActiveCustomerServiceWindow && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="primary"
                                            onClick={handleOpenTemplateDialog}
                                        >
                                            {labels.sendTemplate}
                                        </Button>
                                    )}
                                {canUseConversationActions && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        onClick={handleOpenSessionDialog}
                                    >
                                        {labels.sendSessionMessage}
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                    {canMutate && contactEpisodeActionContexts.length > 0 && (
                        <Box marginTop={2}>
                            <WhatsappResolvedContactEpisodeControls
                                mode={whatsappContactEpisodeMode}
                                companyId={data.companyId}
                                applicantId={data.applicantId}
                                contexts={contactEpisodeActionContexts}
                                selectedContext={
                                    activeContactEpisodeActionContext
                                }
                                onSelect={
                                    setSelectedContactEpisodeActionContext
                                }
                                onReassigned={refreshConversation}
                                notify={showMessage}
                                t={contactEpisodeT}
                            />
                        </Box>
                    )}
                </Paper>

                {messages.length === 0 ? (
                    <Alert severity="info" className={classes.emptyState}>
                        {labels.noMessages}
                    </Alert>
                ) : (
                    <Paper className={classes.paperContainer}>
                        <List className={classes.messageList}>
                            {messages.map((message: Compleo.IObject) => {
                                const isOutgoing =
                                    message.direction === "OUTGOING";
                                const timestamp =
                                    message.metaTimestamp || message.createdAt;
                                const mediaDisplayName = isWhatsappMediaMessage(
                                    message
                                )
                                        ? getWhatsappMediaDisplayName(
                                              message,
                                              labels
                                          )
                                        : "";
                                const caption = String(
                                    message.caption || ""
                                ).trim();
                                const showMediaCaption =
                                    !!caption && caption !== mediaDisplayName;

                                return (
                                    <React.Fragment key={message.messageId}>
                                        <ListItem
                                            alignItems="flex-start"
                                            style={{
                                                justifyContent: isOutgoing
                                                    ? "flex-end"
                                                    : "flex-start"
                                            }}
                                        >
                                            <Box
                                                className={`${
                                                    classes.messageBubble
                                                } ${
                                                    isOutgoing
                                                        ? classes.outgoingBubble
                                                        : classes.incomingBubble
                                                }`}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    component="div"
                                                >
                                                    {isWhatsappMediaMessage(
                                                        message
                                                    ) ? (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gridGap={8}
                                                        >
                                                            <DescriptionIcon fontSize="small" />
                                                            <span>
                                                                {
                                                                    mediaDisplayName
                                                                }
                                                            </span>
                                                        </Box>
                                                    ) : (
                                                        formatWhatsappReactionText(
                                                            message,
                                                            labels
                                                        ) ||
                                                        message.textContent ||
                                                        message.caption ||
                                                        `${labels.mediaType}: ${
                                                            message.type || "-"
                                                        }`
                                                    )}
                                                </Typography>
                                                {isWhatsappMediaMessage(
                                                    message
                                                ) &&
                                                    showMediaCaption && (
                                                        <Typography variant="body2">
                                                            {caption}
                                                        </Typography>
                                                    )}
                                                <Box
                                                    className={
                                                        classes.messageMeta
                                                    }
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={`${
                                                            labels.status
                                                        }: ${getWhatsappProfileStatusLabel(
                                                                message.status,
                                                                labels
                                                        )}`}
                                                        style={{
                                                            backgroundColor: getStatusColor(
                                                                    message.status
                                                                ),
                                                            color: "white"
                                                        }}
                                                    />
                                                    {message.templateName && (
                                                        <Chip
                                                            size="small"
                                                            label={`${labels.templateName}: ${message.templateName}`}
                                                        />
                                                    )}
                                                    <Typography
                                                        variant="caption"
                                                        component="span"
                                                    >
                                                        {formatWhatsappInboxDate(
                                                            timestamp,
                                                            data.language
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <ApplicantWhatsappMediaActions
                                                    message={message}
                                                    mode={
                                                        whatsappContactEpisodeMode
                                                    }
                                                    canMutate={canMutate}
                                                    messageActionId={
                                                        messageActionId
                                                    }
                                                    labels={labels}
                                                    className={
                                                        classes.messageActions
                                                    }
                                                    onDownload={
                                                        handleDownloadMedia
                                                    }
                                                    onSave={handleSaveMedia}
                                                />
                                            </Box>
                                        </ListItem>
                                        <Divider component="li" />
                                    </React.Fragment>
                                );
                            })}
                        </List>
                        {canUseConversationActions && (
                                <Box
                                    className={
                                        classes.conversationFooterActions
                                    }
                                >
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    onClick={handleOpenSessionDialog}
                                >
                                    {labels.sendSessionMessage}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>

            <Dialog
                open={canUseConversationActions && !!consentModalAction}
                onClose={() => {
                    actionRequestTrackerRef.current.clear("SET_CONSENT");
                    setConsentModalAction("");
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {consentModalAction === "OPT_IN"
                        ? labels.consentDialogOptInTitle
                        : labels.consentDialogOptOutTitle}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {consentModalAction === "OPT_IN"
                            ? labels.consentDialogOptInText
                            : labels.consentDialogOptOutText}
                    </DialogContentText>
                    {consentModalAction === "OPT_IN" && (
                        <Box marginTop={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={consentCategories.includes(
                                            "RECRUITING_PROCESS"
                                        )}
                                        onChange={() =>
                                            handleConsentCategoryChange(
                                                "RECRUITING_PROCESS"
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label={labels.recruitingProcessCategory}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={consentCategories.includes(
                                            "FUTURE_OPPORTUNITIES"
                                        )}
                                        onChange={() =>
                                            handleConsentCategoryChange(
                                                "FUTURE_OPPORTUNITIES"
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label={labels.futureOpportunitiesCategory}
                            />
                        </Box>
                    )}
                    <TextField
                        className={classes.textField}
                        fullWidth
                        multiline
                        rows={3}
                        required
                        label={labels.consentEvidence}
                        value={consentEvidence}
                        onChange={(event) =>
                            setConsentEvidence(event.target.value)
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            actionRequestTrackerRef.current.clear(
                                "SET_CONSENT"
                            );
                            setConsentModalAction("");
                        }}
                    >
                        {labels.close}
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSaveConsent}
                        disabled={
                            isSavingConsent ||
                            !consentEvidence.trim() ||
                            (consentModalAction === "OPT_IN" &&
                                (manualOptInBlocked ||
                                    consentCategories.length === 0))
                        }
                    >
                        {labels.saveConsent}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={canUseConversationActions && consentHistoryOpen}
                onClose={() => setConsentHistoryOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{labels.consentHistory}</DialogTitle>
                <DialogContent>
                    {consentHistoryState.status !== "success" ? (
                        <Loading />
                    ) : consentEvents.length === 0 ? (
                        <Alert severity="info">{labels.noConsentEvents}</Alert>
                    ) : (
                        <List>
                            {consentEvents.map((event: Compleo.IObject) => (
                                <ListItem
                                    key={`${event.skGS1pk || event.eventAt}`}
                                    alignItems="flex-start"
                                >
                                    <Box>
                                        <Typography variant="body2">
                                            {`${event.action || "-"} - ${
                                                event.source || "-"
                                            }`}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            component="div"
                                        >
                                            {transformDBData({
                                                value: event.eventAt,
                                                t: data.t,
                                                language: data.language,
                                                replaceOption: "-",
                                                customDateFormat: "dateWithTime"
                                            })}
                                            {event.actorUserEmail
                                                ? ` - ${event.actorUserEmail}`
                                                : ""}
                                        </Typography>
                                        {event.evidenceText && (
                                            <Typography variant="caption">
                                                {event.evidenceText}
                                            </Typography>
                                        )}
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConsentHistoryOpen(false)}>
                        {labels.close}
                    </Button>
                </DialogActions>
            </Dialog>

            <WhatsappTemplateSendDialog
                open={
                    canUseConversationActions && templateModalOpen
                }
                companyId={data.companyId}
                applicantId={data.applicantId}
                language={data.language}
                defaultJobId={data.jobDataInsideJob?.jobId}
                jobOptions={applicantJobOptions}
                contactEpisodeActionContext={
                    activeContactEpisodeActionContext
                }
                onClose={() => setTemplateModalOpen(false)}
                onSent={refreshConversation}
                showMessage={showMessage}
            />

            <Dialog
                open={
                    canUseConversationActions && sessionModalOpen
                }
                onClose={() => {
                    actionRequestTrackerRef.current.clear("SEND_SESSION");
                    setSessionModalOpen(false);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{labels.sessionDialogTitle}</DialogTitle>
                <DialogContent>
                    <input
                        ref={sessionFileInputRef}
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/jpeg,image/png"
                        onChange={(event) => {
                            setSessionFile(event.target.files?.[0] || null);
                            event.target.value = "";
                        }}
                    />
                    <TextField
                        className={classes.textField}
                        fullWidth
                        multiline
                        rows={4}
                        label={
                            sessionFile
                                ? labels.sessionCaptionField
                                : labels.sessionMessageField
                        }
                        value={sessionMessage}
                        onChange={(event) =>
                            setSessionMessage(event.target.value)
                        }
                    />
                    <Box
                        display="flex"
                        alignItems="center"
                        gridGap={8}
                        marginTop={1}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AttachFileIcon />}
                            onClick={() => sessionFileInputRef.current?.click()}
                        >
                            {labels.attachFile}
                        </Button>
                        {sessionFile && (
                            <>
                                <Typography variant="caption">
                                    {sessionFile.name}
                                </Typography>
                                <ButtonIconTooltipCompleo
                                    label={labels.removeAttachment}
                                    onClick={() => setSessionFile(null)}
                                >
                                    <CloseIcon />
                                </ButtonIconTooltipCompleo>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            actionRequestTrackerRef.current.clear(
                                "SEND_SESSION"
                            );
                            setSessionModalOpen(false);
                            setSessionFile(null);
                        }}
                    >
                        {labels.close}
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSendSessionMessage}
                        disabled={
                            isSendingSession ||
                            (!sessionMessage.trim() && !sessionFile)
                        }
                    >
                        {labels.send}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default ApplicantWhatsapp;
